/**
 * EquipmentSymbol.jsx — ISA-style engineering symbols for PFD/P&ID
 *
 * Replaces the plain rectangle that BaseEngineeringEditor used to draw
 * with a proper engineering symbol that visually communicates the
 * equipment type at-a-glance (the way SmartPlant / AutoCAD P&ID do).
 *
 * Each symbol is centred inside an (eq.w × eq.h) bounding box so it
 * scales with the user-configurable width / height in the properties
 * panel.
 *
 * Props:
 *   - type     — eq.type ("vessel" / "pump" / "heat_exchanger" / …)
 *   - w, h     — bounding box
 *   - stroke   — stroke colour
 *   - strokeWidth
 *   - fill     — body fill
 */
import React from "react";

export default function EquipmentSymbol({
   type, w, h, stroke, strokeWidth = 2, fill = "#0f1729",
}) {
   const t = (type || "vessel").toLowerCase();
   const sp = { stroke, strokeWidth, fill };
   switch (t) {
      case "pump":         return <PumpSym         w={w} h={h} {...sp}/>;
      case "compressor":   return <CompressorSym   w={w} h={h} {...sp}/>;
      case "heat_exchanger": return <HeatExSym     w={w} h={h} {...sp}/>;
      case "column":
      case "tower":
      case "distillation": return <ColumnSym       w={w} h={h} {...sp}/>;
      case "tank":         return <TankSym         w={w} h={h} {...sp}/>;
      case "reactor":      return <ReactorSym      w={w} h={h} {...sp}/>;
      case "valve":
      case "control_valve":
      case "fv":           return <ValveSym        w={w} h={h} {...sp}/>;
      case "filter":
      case "strainer":     return <FilterSym       w={w} h={h} {...sp}/>;
      case "vessel":
      case "drum":
      case "separator":
      default:             return <VesselSym       w={w} h={h} {...sp}/>;
   }
}

/* ── Vertical vessel: rectangle with elliptical heads ───────────────── */
function VesselSym({ w, h, stroke, strokeWidth, fill }) {
   const headH = Math.min(h * 0.18, w * 0.4);
   return (
      <>
         <path d={`M 0 ${headH}
                       L 0 ${h - headH}
                       Q 0 ${h} ${w / 2} ${h}
                       Q ${w} ${h} ${w} ${h - headH}
                       L ${w} ${headH}
                       Q ${w} 0 ${w / 2} 0
                       Q 0 0 0 ${headH} Z`}
               fill={fill} stroke={stroke} strokeWidth={strokeWidth}/>
      </>
   );
}

/* ── Centrifugal pump: circle with arrowhead triangle ───────────────── */
function PumpSym({ w, h, stroke, strokeWidth, fill }) {
   const r = Math.min(w, h) * 0.42;
   const cx = w / 2, cy = h / 2;
   return (
      <>
         <circle cx={cx} cy={cy} r={r}
                  fill={fill} stroke={stroke} strokeWidth={strokeWidth}/>
         {/* discharge nozzle stub (top) */}
         <line x1={cx} y1={cy - r} x2={cx} y2={0}
                stroke={stroke} strokeWidth={strokeWidth}/>
         {/* suction nozzle stub (left) */}
         <line x1={cx - r} y1={cy} x2={0} y2={cy}
                stroke={stroke} strokeWidth={strokeWidth}/>
         {/* impeller arrow triangle */}
         <polygon points={`${cx - r * 0.6},${cy - r * 0.5}
                                  ${cx + r * 0.6},${cy}
                                  ${cx - r * 0.6},${cy + r * 0.5}`}
                    fill={stroke} stroke="none" opacity="0.8"/>
      </>
   );
}

/* ── Centrifugal compressor: trapezoid ─────────────────────────────── */
function CompressorSym({ w, h, stroke, strokeWidth, fill }) {
   return (
      <>
         <polygon points={`0,${h * 0.2} ${w},0 ${w},${h} 0,${h * 0.8}`}
                    fill={fill} stroke={stroke} strokeWidth={strokeWidth}/>
         <line x1={w * 0.5} y1={h * 0.1} x2={w * 0.5} y2={h * 0.9}
                stroke={stroke} strokeWidth={strokeWidth * 0.6}
                strokeDasharray="2 2" opacity="0.5"/>
      </>
   );
}

/* ── Shell-and-tube heat exchanger: rounded rectangle + tubes ──────── */
function HeatExSym({ w, h, stroke, strokeWidth, fill }) {
   const r = h * 0.5;
   return (
      <>
         <path d={`M ${r} 0
                       L ${w - r} 0
                       Q ${w} 0 ${w} ${r}
                       L ${w} ${h - r}
                       Q ${w} ${h} ${w - r} ${h}
                       L ${r} ${h}
                       Q 0 ${h} 0 ${h - r}
                       L 0 ${r}
                       Q 0 0 ${r} 0 Z`}
               fill={fill} stroke={stroke} strokeWidth={strokeWidth}/>
         {/* tube bundle hint */}
         <line x1={r} y1={h * 0.5} x2={w - r} y2={h * 0.5}
                stroke={stroke} strokeWidth={strokeWidth * 0.5} opacity="0.5"/>
         <line x1={r} y1={h * 0.35} x2={w - r} y2={h * 0.35}
                stroke={stroke} strokeWidth={strokeWidth * 0.5} opacity="0.5"/>
         <line x1={r} y1={h * 0.65} x2={w - r} y2={h * 0.65}
                stroke={stroke} strokeWidth={strokeWidth * 0.5} opacity="0.5"/>
      </>
   );
}

/* ── Distillation column / tower: tall vessel with internal trays ──── */
function ColumnSym({ w, h, stroke, strokeWidth, fill }) {
   const headH = Math.min(h * 0.10, w * 0.4);
   const nTrays = Math.min(8, Math.floor(h / 24));
   const trayPitch = (h - headH * 2) / (nTrays + 1);
   const trays = [];
   for (let i = 1; i <= nTrays; i++) {
      const ty = headH + i * trayPitch;
      trays.push(
         <line key={i} x1={w * 0.15} y1={ty} x2={w * 0.85} y2={ty}
                stroke={stroke} strokeWidth={strokeWidth * 0.5} opacity="0.7"/>
      );
   }
   return (
      <>
         <path d={`M 0 ${headH}
                       L 0 ${h - headH}
                       Q 0 ${h} ${w / 2} ${h}
                       Q ${w} ${h} ${w} ${h - headH}
                       L ${w} ${headH}
                       Q ${w} 0 ${w / 2} 0
                       Q 0 0 0 ${headH} Z`}
               fill={fill} stroke={stroke} strokeWidth={strokeWidth}/>
         {trays}
      </>
   );
}

/* ── Storage tank: cylinder with flat top ──────────────────────────── */
function TankSym({ w, h, stroke, strokeWidth, fill }) {
   return (
      <>
         <ellipse cx={w / 2} cy={h * 0.08} rx={w / 2 - 1} ry={h * 0.06}
                    fill={fill} stroke={stroke} strokeWidth={strokeWidth}/>
         <path d={`M 0 ${h * 0.08} L 0 ${h * 0.94}
                       Q 0 ${h} ${w / 2} ${h}
                       Q ${w} ${h} ${w} ${h * 0.94}
                       L ${w} ${h * 0.08}`}
               fill={fill} stroke={stroke} strokeWidth={strokeWidth}/>
      </>
   );
}

/* ── Reactor: vessel + cross-pattern agitator ──────────────────────── */
function ReactorSym({ w, h, stroke, strokeWidth, fill }) {
   const headH = Math.min(h * 0.15, w * 0.4);
   return (
      <>
         <path d={`M 0 ${headH}
                       L 0 ${h - headH}
                       Q 0 ${h} ${w / 2} ${h}
                       Q ${w} ${h} ${w} ${h - headH}
                       L ${w} ${headH}
                       Q ${w} 0 ${w / 2} 0
                       Q 0 0 0 ${headH} Z`}
               fill={fill} stroke={stroke} strokeWidth={strokeWidth}/>
         {/* Agitator shaft + impeller */}
         <line x1={w / 2} y1={0} x2={w / 2} y2={h * 0.6}
                stroke={stroke} strokeWidth={strokeWidth}/>
         <line x1={w * 0.25} y1={h * 0.6} x2={w * 0.75} y2={h * 0.6}
                stroke={stroke} strokeWidth={strokeWidth}/>
         <line x1={w * 0.35} y1={h * 0.55} x2={w * 0.65} y2={h * 0.65}
                stroke={stroke} strokeWidth={strokeWidth * 0.7}/>
      </>
   );
}

/* ── Valve: bow-tie shape ───────────────────────────────────────────── */
function ValveSym({ w, h, stroke, strokeWidth, fill }) {
   return (
      <>
         <polygon points={`0,0 ${w},${h} ${w},0 0,${h}`}
                    fill={fill} stroke={stroke} strokeWidth={strokeWidth}/>
         {/* Optional stem (control-valve hint) */}
         <line x1={w / 2} y1={h / 2} x2={w / 2} y2={-h * 0.3}
                stroke={stroke} strokeWidth={strokeWidth * 0.6}/>
      </>
   );
}

/* ── Filter / strainer: rectangle with diagonal mesh ───────────────── */
function FilterSym({ w, h, stroke, strokeWidth, fill }) {
   return (
      <>
         <rect width={w} height={h} fill={fill} stroke={stroke}
                strokeWidth={strokeWidth} rx="2"/>
         <line x1={0} y1={0} x2={w} y2={h}
                stroke={stroke} strokeWidth={strokeWidth * 0.5}/>
         <line x1={0} y1={h} x2={w} y2={0}
                stroke={stroke} strokeWidth={strokeWidth * 0.5}/>
      </>
   );
}
